import React from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import DrawIcon from '@mui/icons-material/Draw';
import {useTranslation} from 'react-i18next';

interface ContractPreviewDialogProps {
    open: boolean;
    url: string | null;
    loading: boolean;
    signed: boolean;
    signing: boolean;
    onClose: () => void;
    onSign: () => void;
    onDownload: () => void;
}

export default function ContractPreviewDialog({
                                                  open,
                                                  url,
                                                  loading,
                                                  signed,
                                                  signing,
                                                  onClose,
                                                  onSign,
                                                  onDownload,
                                              }: ContractPreviewDialogProps): React.ReactElement {
    const {t} = useTranslation();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Dialog open={open} onClose={signing ? undefined : onClose} fullScreen={fullScreen} maxWidth="lg" fullWidth>
            <DialogTitle sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                {t('contracts.preview.title')}
                {signed && (
                    <Chip
                        size="small"
                        color="success"
                        icon={<VerifiedIcon/>}
                        label={t('contracts.preview.signed')}
                    />
                )}
            </DialogTitle>
            <DialogContent dividers sx={{p: 0, height: fullScreen ? '100%' : '75vh'}}>
                {loading || !url ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
                        <CircularProgress/>
                    </Box>
                ) : (
                    <iframe
                        title={t('contracts.preview.title')}
                        src={url}
                        style={{border: 0, width: '100%', height: '100%'}}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={signing}>{t('common.actions.close')}</Button>
                <Button onClick={onDownload} disabled={loading || !url}>
                    {t('contracts.actions.downloadPdf')}
                </Button>
                {!signed && (
                    <Button
                        variant="contained"
                        onClick={onSign}
                        disabled={loading || !url || signing}
                        startIcon={signing ? <CircularProgress size={16} color="inherit"/> : <DrawIcon/>}
                    >
                        {t('contracts.actions.sign')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
